import { createStyles } from "antd-style";
import type { OInputSizeType } from "../types";
import { ospThemes } from "../../ospThemes";

export const useStyles = createStyles(
	(
		{ css },
		{
			focusState,
			errorState,
			disable,
			size,
			borderVisible,
			placeholderColor,
			focusBorderColor,
		}: {
			focusState: boolean;
			errorState: boolean;
			disable: boolean;
			size: OInputSizeType;
			borderVisible?: boolean;
			placeholderColor?: string;
			focusBorderColor?: string;
		},
	) => {
		const borderColor = !borderVisible
			? "transparent"
			: errorState
				? ospThemes.colors.content_accent2_primary
				: focusState
					? focusBorderColor ? focusBorderColor : ospThemes.colors.content_brand_primary
					: "transparent";
		const backgroundColor = !borderVisible
			? "transparent"
			: disable
				? ospThemes.colors.background_white_overlay20
				: ospThemes.colors.background_white_overlay10;
		const textColor = !borderVisible
			? ospThemes.colors.content_primary
			: errorState
				? ospThemes.colors.content_accent2_primary
				: ospThemes.colors.content_primary;

		return {
			/* Override browser autofill ugly styles within OSP input container */
			rootContainer: css`
			position: relative;
			background-color: ${backgroundColor};
			
			input:-webkit-autofill,
			input:-webkit-autofill:hover,
			input:-webkit-autofill:focus {
				-webkit-text-fill-color: ${textColor};
				box-shadow: 0 0 0 1000px ${backgroundColor} inset;
				border-radius: inherit;
				transition: background-color 9999s ease-out, color 9999s ease-out;
			}

			input:autofill {
				/* For browsers that support the standard :autofill pseudo-class */
				-webkit-text-fill-color: ${textColor};
				box-shadow: 0 0 0 1000px ${backgroundColor} inset;
				border-radius: inherit;
				transition: background-color 9999s ease-out, color 9999s ease-out;
			}

			border-color: ${borderColor};
			border-radius: 8px;
			border-width: 1px;
			display: flex;
			align-items: center;
			flex-direction: row;
			width: 100%;
			height: ${size === "large" ? 56 : 48}px;
			padding: 0px 12px;

			input, textarea {
				color: ${textColor};
				outline: none;
				width: 100%;
				height: 100%;
				background-color: transparent;
				caret-color: ${ospThemes.colors.content_brand_primary};
				position: relative;
				z-index: 2;
				&::placeholder {
					color: transparent;
				}
			}
			textarea {
				min-height: 18px !important;
				padding-top: 0;
				padding-bottom: 0;
				resize: none;
			}
			
			/* Control placeholderText visibility based on textarea state */
			/* Only hide when textarea has content, keep visible when focused but empty */
			/* Use :has() selector for modern browsers */
			&:has(textarea:not(:placeholder-shown)) .placeholder-text {
				opacity: 0;
			}
			
			/* Fallback for browsers that don't support :has() - direct sibling selector */
			textarea:not(:placeholder-shown) ~ .placeholder-text {
				opacity: 0;
			}
			`,
			textAreaContainer: css`
				height: auto;
			`,
			textArea: css`
				height: auto;
			`,
			suffix: css`
				display: inline-flex;
				flex: none;
				position: relative;
				flex-direction: row;
				align-items: center;
			`,
			prefix: css`
				display: inline-flex;
				flex: none;
				flex-direction: row;
				align-items: center;
				position: relative;

			`,
			marginPrefix: css`
				margin-inline-end: 12px;
			`,
			marginSuffix: css`
				margin-inline-start: 12px;
			`,
			affixWrapper: css`
				position: relative;
				display: inline-flex;
				flex-direction: row;
				align-items: center;
				width: 100%;
				&::before {
					display: inline-block;
					width: 0;
					visibility: hidden;
					content: '\a0';
				}
			`,
			placeholderText: css`
				position: absolute;
				left: 12px;
				top: 0;
				pointer-events: none;
				z-index: 1;
				color: ${placeholderColor || ospThemes.colors.content_primary};
				transition: opacity 0.2s ease;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
				max-width: calc(100% - 24px);
			`,
		};
	},
	{ hashPriority: "low" },
);
